import { PERSONA_TONE_NAME_MAX, PERSONA_TONE_TEXT_MAX } from '@matter_extractor/shared';
import type { PersonaToneWrite } from '@matter_extractor/shared';
import { Button, TextArea, TextField } from '@pms-digital/design-system';
import { useCallback, useId, useState } from 'react';

/**
 * « Ajouter un ton » - the modal of the editor (refonte modulaire §10.1;
 * maquette `AjouterTon`).
 *
 * It creates the tone IN THE LIBRARY of §11.5 and selects it for this persona,
 * which is the whole gesture: « on crée un ton au moment où on en a besoin, on
 * l'entretient dans Réglages ». Renaming, rewriting and deleting are not here
 * and never will be - they are the settings tab's, and the note under the tag
 * cloud says so.
 *
 * ## Why it is not `VersionedDialog`
 *
 * That shell exists for the three boxes that SHOW AN EFFECT before it happens,
 * and it puts the focus on « Annuler » on mount so a stray Enter can never
 * commit a version, move a pointer or retire an entity. This box asks for two
 * fields instead, and the first thing an operator does in it is type - focus on
 * a cancel button at the END of the tab order would make them tab backwards to
 * reach the field they opened the box for. The shell below is otherwise the
 * same one, down to the `--backdrop` token: a native `<dialog open>` kept in
 * flow by React state, Escape wired by hand because `open` alone does not wire
 * it.
 *
 * ## The refusal is worded by the CALLER
 *
 * H16: a name already taken is refused BY NAMING the other tone, never
 * overwritten in silence. That sentence is `personaReferenceMessage` in
 * `lib/persona-labels.ts`, already written for the settings tab, and it arrives
 * here as a string - this file words nothing and parses nothing.
 */

export interface PersonaToneDialogProps {
  busy: boolean;
  /** The refusal of the last attempt, worded from its `code`, or null. */
  error: string | null;
  onCreate: (input: PersonaToneWrite) => void;
  onCancel: () => void;
}

export function PersonaToneDialog({ busy, error, onCreate, onCancel }: PersonaToneDialogProps) {
  const titleId = useId();
  const [name, setName] = useState('');
  const [text, setText] = useState('');

  // A `useCallback` ref rather than `autoFocus` (the a11y lint refuses it) and
  // rather than an effect - a stable callback runs on attach and on nothing else.
  const focusOnMount = useCallback((node: HTMLInputElement | null) => node?.focus(), []);

  const ready = name.trim() !== '' && text.trim() !== '';

  return (
    <div
      data-testid="persona-boite-ajouter-ton"
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-[var(--backdrop)] p-5 py-10"
    >
      <dialog
        open
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
        className="relative m-0 w-full max-w-xl space-y-5 rounded-md border-2 border-gray-200 bg-white p-6 shadow-lg"
      >
        <div className="space-y-1">
          <h2 id={titleId} className="font-semibold text-bleu-nuit text-h4">
            Ajouter un ton
          </h2>
          <p className="text-gray-700 text-small">
            Le ton entre dans la bibliothèque et se sélectionne pour ce persona. La bibliothèque
            n’est pas versionnée : la version du persona en garde une copie.
          </p>
        </div>

        {/*
          240 px and not the full width, which is the one écart the maquette
          assumes twice - here and on the persona's first name: « un champ
          pleine largeur pour "chaleureux" ment sur ce qu'on attend ». The width
          is the envelope's: the package field fills it.
        */}
        <div className="w-60 max-w-full">
          <TextField
            label="Nom du ton"
            ref={focusOnMount}
            data-testid="persona-nom-ton"
            value={name}
            maxLength={PERSONA_TONE_NAME_MAX}
            placeholder="posé et lent"
            onChange={(event) => setName(event.currentTarget.value)}
            help={`${PERSONA_TONE_NAME_MAX} caractères. Unique dans la bibliothèque.`}
          />
        </div>

        <div className="space-y-2">
          <TextArea
            label="Texte du ton"
            data-testid="persona-texte-ton"
            value={text}
            rows={4}
            maxLength={PERSONA_TONE_TEXT_MAX}
            placeholder="Phrases longues, peu de questions d’affilée, tu laisses des silences."
            onChange={(event) => setText(event.currentTarget.value)}
          />
          <p className="text-right text-caption text-gray-500 leading-4">
            {text.length} / {PERSONA_TONE_TEXT_MAX} caractères
          </p>
        </div>

        {/* Mounted from the first render, empty or not: a live region created at
            the same moment it fills is not read out. */}
        <p aria-live="polite" className="text-rouge-deep text-small">
          {error}
        </p>

        <div className="flex flex-wrap justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="button"
            variant="secondary"
            data-testid="persona-confirmer-ton"
            disabled={busy || !ready}
            onClick={() => onCreate({ name: name.trim(), text: text.trim() })}
          >
            Ajouter
          </Button>
        </div>

        <p className="text-caption text-gray-600 leading-4">
          Un nom déjà pris est refusé en nommant le ton existant : rien n’est écrasé en silence.
        </p>
      </dialog>
    </div>
  );
}
